import {
  AutoFixHigh as AutomationIcon,
  BarChart as ReportsIcon,
  MenuBook as DictionaryIcon,
  DirectionsBoat as DirectionsBoatIcon,
  Loyalty,
  Man as ManIcon,
  Map as MapIcon,
  Inventory as PackageIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import NoteIcon from "@mui/icons-material/NoteAlt";
import PhoneIcon from "@mui/icons-material/Phone";
import { Box, Grid, Stack, Tab, Tabs } from "@mui/material";
import throttle from "lodash/throttle";
import type { FC } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  canAccessStatistics,
  isAgentWithPackagesAccess,
  isAgentWithProductsAndProviderAccess,
  isAgentWithProvidersProductsAndDictionariesAccess,
} from "../auth/auth";
import { useAuthContext } from "../auth/AuthContext";
import TextSearch from "../components/textSearch/TextSearch";
import AgentCallsSearch from "./agentCalls/AgentCallsSearch";
import CustomerNotesSearch from "./customerNotes/CustomerNotesSearch";
import ProductSearch from "./ProductSearch";
import type { ChangeFilters, Filters } from "./useAdminFilters";

type ResourceType =
  | "agents"
  | "providers"
  | "products"
  | "packages"
  | "automation"
  | "global-settings"
  | "dictionaries"
  | "loyalty"
  | "rewards"
  | "customer-notes"
  | "phone-calls"
  | "statistics";

function pathnameToResourceType(pathname: string): ResourceType | "admin" {
  const pathSegments = pathname.split("/");
  if (pathSegments.includes("products")) {
    return "products";
  }
  if (pathSegments.includes("packages")) {
    return "packages";
  }
  if (pathSegments.includes("providers")) {
    return "providers";
  }
  if (pathSegments.includes("agents")) {
    return "agents";
  }
  if (pathSegments.includes("loyalty")) {
    return "loyalty";
  }
  if (pathSegments.includes("rewards")) {
    return "rewards";
  }
  if (pathSegments.includes("customer-notes")) {
    return "customer-notes";
  }
  if (pathSegments.includes("phone-calls")) {
    return "phone-calls";
  }
  if (pathSegments.includes("statistics")) {
    return "statistics";
  }
  return pathSegments[pathSegments.length - 1] as ResourceType | "admin";
}

interface SearchProps {
  readonly resourceType: ResourceType;
  readonly filters: Filters;
  onChange: ChangeFilters;
}

const Search: FC<SearchProps> = ({ resourceType, filters, onChange }) => {
  if (resourceType === "automation" || resourceType === "global-settings" || resourceType === "dictionaries") {
    return null;
  }
  if (resourceType === "statistics") {
    return null;
  }
  if (resourceType === "customer-notes") {
    return <CustomerNotesSearch filters={filters["customer-notes"]} onChange={(f) => onChange("customer-notes", f)} />;
  }
  if (resourceType === "phone-calls") {
    return <AgentCallsSearch filters={filters["phone-calls"]} onChange={(f) => onChange("phone-calls", f)} />;
  }
  if (resourceType === "products") {
    return <ProductSearch filters={filters["products"]} onChange={(f) => onChange("products", f)} />;
  }
  return <TextSearch value={filters[resourceType].query} onChange={(q) => onChange(resourceType, { query: q })} />;
};

interface Props {
  readonly filters: Filters;
  readonly onFiltersChange: ChangeFilters;
}

const AdminPage: FC<Props> = ({ filters, onFiltersChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { context } = useAuthContext();
  const resourceType = pathnameToResourceType(location.pathname);
  if (resourceType === "admin") {
    return <Outlet context={filters} />;
  }

  const handleTabChange = throttle(
    (newValue) => {
      navigate(newValue);
    },
    100,
    { leading: true, trailing: false },
  );

  const isAdmin = context?.role === "ADMIN";
  const agentCanViewProvidersAndProducts = isAgentWithProductsAndProviderAccess(context);
  const agentCanEditDictionaries = isAgentWithProvidersProductsAndDictionariesAccess(context);
  const agentCanEditPackages = isAgentWithPackagesAccess(context);
  const canViewStatistics = canAccessStatistics(context);
  return (
    <Stack sx={{ height: "100%" }}>
      <Grid container spacing={2} sx={{ marginBottom: 1 }}>
        <Grid size={{ xs: 12, xl: 7 }} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={resourceType}
            onChange={(_, newValue) => handleTabChange(newValue)}
            onClick={() => handleTabChange(resourceType)}
            aria-label="zasoby"
            variant="scrollable"
            scrollButtons="auto"
          >
            {isAdmin && <Tab label="Przedstawiciele" icon={<ManIcon />} iconPosition="start" value="agents" />}
            {(isAdmin || agentCanViewProvidersAndProducts) && (
              <Tab label="Dostawcy" icon={<DirectionsBoatIcon />} iconPosition="start" value="providers" />
            )}
            {(isAdmin || agentCanViewProvidersAndProducts) && (
              <Tab label="Produkty" icon={<MapIcon />} iconPosition="start" value="products" />
            )}
            {(isAdmin || agentCanEditPackages) && (
              <Tab label="Pakiety" icon={<PackageIcon />} iconPosition="start" value="packages" />
            )}
            {isAdmin && <Tab label="Automatyzacja" icon={<AutomationIcon />} iconPosition="start" value="automation" />}
            {isAdmin && (
              <Tab label="Ustawienia globalne" icon={<SettingsIcon />} iconPosition="start" value="global-settings" />
            )}
            {(isAdmin || agentCanEditDictionaries) && (
              <Tab label="Słowniki" icon={<DictionaryIcon />} iconPosition="start" value="dictionaries" />
            )}
            {isAdmin && <Tab label="Punkty" icon={<Loyalty />} iconPosition="start" value="loyalty" />}
            {isAdmin && <Tab label="Nagrody" icon={<EmojiEventsIcon />} iconPosition="start" value="rewards" />}
            {isAdmin && (
              <Tab label="Notatki klientów" icon={<NoteIcon />} iconPosition="start" value="customer-notes" />
            )}
            {isAdmin && <Tab label="Telefony" icon={<PhoneIcon />} iconPosition="start" value="phone-calls" />}
            {canViewStatistics && (
              <Tab label="Raporty" icon={<ReportsIcon />} iconPosition="start" value="statistics" />
            )}
          </Tabs>
        </Grid>
        <Grid container size={{ xs: 12, xl: 5 }} sx={{ alignItems: "center" }}>
          <Grid size={12}>
            <Search resourceType={resourceType} filters={filters} onChange={onFiltersChange} />
          </Grid>
        </Grid>
      </Grid>
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        <Outlet context={filters} />
      </Box>
    </Stack>
  );
};

export default AdminPage;
